import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import GoogleGlyph from './GoogleGlyph';
import JourneyAnimation from './JourneyAnimation';
import {
  CONTENTS_LABEL,
  CTA,
  HERO,
  PAGE_SECTIONS,
  PRACTICES,
  STATUS_LABEL,
  heroSupportingCopy,
  heroTrustStrip,
  notifyMeHref,
  sectionNumber,
  statusLine,
  type Practice,
  type PracticeKey,
} from './homepageContent';

/**
 * Landing hero — the three practices as MANUAL tabs (the WAI-ARIA tabs pattern), Criminal
 * selected by default. A tab changes only on a click, tap or key press; nothing rotates on a
 * timer (brief, 2026-11-01). Criminal is the live product: its panel carries the proposition,
 * the trial and sign-in actions and the trust strip. Civil and Corporate are a short teaser
 * with "Notify me" — never a trial path.
 *
 * Kept from the chambers-ledger hero: the High Court photo under its dark wash, the §
 * watermark, the marginalia column, the column widths, and on the right the criminal journey
 * animation from xl (≥1280px) — on EVERY tab: picking Civil or Corporate changes the text
 * column only, never the animation (Ajay, 2026-11-01). Below xl and under reduced motion the
 * right column is the page's table of contents instead. The three panels share one grid cell
 * (inactive ones are `invisible`, which also hides them from assistive tech), so switching
 * tabs never moves anything below the hero.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass focus-visible:outline-offset-2';

interface LandingHeroProps {
  onLogin: () => void;
  loading: boolean;
  error: Error | null;
}

function CriminalPanel({ practice, onLogin, loading, error }: LandingHeroProps & { practice: Practice }) {
  const trust = heroTrustStrip();
  return (
    <>
      <p className="font-plex text-[11px] sm:text-[12px] font-medium tracking-[0.24em] text-brass">
        {statusLine(practice)}
      </p>
      <h1 className="mt-4 font-display font-normal text-[36px] sm:text-5xl xl:text-[clamp(44px,3.1vw,64px)] leading-[1.08] text-parchment tracking-[0.01em] [text-wrap:balance]">
        {HERO.headlineLead}{' '}
        <em className="font-editorial italic text-brass">{HERO.headlineEmphasis}</em>
      </h1>
      <p className="text-base sm:text-[17px] leading-[1.65] sm:leading-[1.7] text-chamber-muted-bright max-w-[560px] mt-5 sm:mt-6">
        {heroSupportingCopy()}
      </p>

      {error && (
        <div
          role="alert"
          className="mt-6 border border-crimson-500/40 bg-crimson-950/30 px-4 py-3 text-sm text-crimson-300 max-w-[520px]"
        >
          {error.message}
        </div>
      )}

      <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="button"
          onClick={onLogin}
          disabled={loading}
          className={`bg-brass text-chamber-ink px-8 py-4 text-sm font-semibold tracking-[0.1em] uppercase hover:bg-brass-hover transition-colors disabled:opacity-50 cursor-pointer ${FOCUS}`}
        >
          {CTA.trial}
        </button>
        {/* Secondary on purpose: the same Google popup (that is how you sign in either way),
            but a returning advocate should not wonder whether it restarts a trial. */}
        <button
          type="button"
          onClick={onLogin}
          disabled={loading}
          className={`text-[#DCE5DA] px-2 py-[15px] text-sm font-medium hover:text-brass transition-colors flex items-center justify-center gap-2.5 disabled:opacity-50 cursor-pointer underline underline-offset-4 decoration-chamber-border-hi hover:decoration-brass ${FOCUS}`}
        >
          <GoogleGlyph />
          {CTA.signIn}
        </button>
      </div>

      {/* Each item carries its own marker, so a wrapped line never starts with a separator. */}
      <ul
        aria-label="Trust"
        className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1.5 font-plex text-[11px] tracking-[0.08em] text-chamber-muted max-w-[600px]"
      >
        {trust.map((item) => (
          <li key={item} className="flex items-center gap-2">
            <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-brass/70" />
            {item}
          </li>
        ))}
      </ul>
    </>
  );
}

function ComingSoonPanel({ practice }: { practice: Practice }) {
  return (
    <>
      <p className="font-plex text-[11px] sm:text-[12px] font-medium tracking-[0.24em] text-chamber-muted">
        {statusLine(practice)}
      </p>
      <h2 className="mt-4 font-display font-normal text-[36px] sm:text-5xl leading-[1.08] text-parchment tracking-[0.01em]">
        {practice.name}
      </h2>
      <p className="text-base sm:text-[17px] leading-[1.65] text-chamber-muted-bright max-w-[520px] mt-5 sm:mt-6">
        {practice.card}
      </p>
      <a
        href={notifyMeHref(practice)}
        className={`mt-7 sm:mt-8 inline-block border border-brass text-brass px-6 py-3 text-[12px] font-semibold tracking-[0.1em] uppercase hover:bg-brass hover:text-chamber-ink transition-colors ${FOCUS}`}
      >
        {CTA.notify}
      </a>
    </>
  );
}

export default function LandingHero({ onLogin, loading, error }: LandingHeroProps) {
  const [active, setActive] = useState<PracticeKey>('criminal');
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const practice = PRACTICES.find((p) => p.key === active) ?? PRACTICES[0];
  const showJourney = !reducedMotion;

  // Arrow keys move along the tabs (wrapping), Home/End jump to the ends; selection follows
  // focus — every panel is already rendered, so there is nothing to load.
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = PRACTICES.length - 1;
    const next =
      e.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
      : e.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : -1;
    if (next < 0) return;
    e.preventDefault();
    setActive(PRACTICES[next].key);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      aria-label="iAdvocate practices"
      className="relative px-5 sm:px-8 lg:px-14 pt-8 sm:pt-14 lg:pt-20 pb-14 sm:pb-20 overflow-hidden"
    >
      {/* Background layers: High Court photo (bottom), then a left→right dark wash so the
          text column stays legible while the courthouse reads through on the right, and a
          soft top/bottom feather. */}
      <img
        src="/images/hero-chambers.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-[75%_center] pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(100deg, rgba(13,22,15,0.97) 0%, rgba(13,22,15,0.92) 38%, rgba(13,22,15,0.62) 62%, rgba(13,22,15,0.30) 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(13,22,15,0.55) 0%, rgba(13,22,15,0) 22%), linear-gradient(to top, rgba(13,22,15,0.85) 0%, rgba(13,22,15,0) 30%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_78%_40%,rgba(201,162,39,0.12),transparent_58%)]"
      />
      <span
        aria-hidden="true"
        className="absolute -right-16 -top-24 font-editorial text-[640px] leading-none text-[rgba(201,162,39,0.05)] select-none pointer-events-none hidden lg:block"
      >
        §
      </span>

      <div
        className={`relative lg:grid lg:grid-cols-[64px_minmax(0,760px)_1fr] gap-12 ${
          showJourney ? 'xl:grid-cols-[64px_minmax(0,clamp(540px,38vw,760px))_1fr] xl:gap-8' : ''
        }`}
      >
        {/* Marginalia column */}
        <div aria-hidden="true" className="hidden lg:flex flex-col border-r border-brass-hairline-soft pr-5">
          <span className="[writing-mode:vertical-rl] font-plex text-[11px] font-medium tracking-[0.32em] text-chamber-muted-faint">
            {practice.marginalia}
          </span>
        </div>

        {/* Main column */}
        <div>
          <div role="tablist" aria-label="Practice areas" className="flex gap-5 items-baseline mb-5 sm:mb-7">
            {PRACTICES.map((p, i) => {
              const selected = p.key === active;
              return (
                <button
                  key={p.key}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`hero-tab-${p.key}`}
                  aria-selected={selected}
                  aria-controls={`hero-panel-${p.key}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(p.key)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                  aria-label={p.status === 'beta_live' ? undefined : `${p.tab}, ${STATUS_LABEL[p.status]}`}
                  className={`font-editorial italic text-[15px] sm:text-[16px] pt-2 pb-1 border-b transition-colors cursor-pointer ${FOCUS} ${
                    selected
                      ? 'text-brass border-brass'
                      : 'text-chamber-muted border-transparent hover:text-parchment'
                  }`}
                >
                  {p.tab}
                </button>
              );
            })}
          </div>

          <div className="grid">
            {PRACTICES.map((p) => (
              <div
                key={p.key}
                role="tabpanel"
                id={`hero-panel-${p.key}`}
                aria-labelledby={`hero-tab-${p.key}`}
                className={`col-start-1 row-start-1 ${p.key === active ? '' : 'invisible'}`}
              >
                {p.key === 'criminal' ? (
                  <CriminalPanel practice={p} onLogin={onLogin} loading={loading} error={error} />
                ) : (
                  <ComingSoonPanel practice={p} />
                )}
              </div>
            ))}
          </div>
        </div>

        {showJourney && (
          // Top-aligned with the tabs, not centred (owner, 2026-10-17).
          <JourneyAnimation className="hidden xl:block self-start mt-2" />
        )}

        {/* Table of contents — where the journey is not shown. */}
        <aside
          aria-label="On this page"
          className={`hidden ${showJourney ? 'lg:block xl:hidden' : 'lg:block'} self-center`}
        >
          <p className="font-plex text-[11px] tracking-[0.3em] text-chamber-muted-dim pb-4 border-b border-brass-hairline-soft">
            {CONTENTS_LABEL}
          </p>
          {PAGE_SECTIONS.map((row, i) => (
            <a
              key={row.id}
              href={`#${row.id}`}
              className={`flex items-baseline gap-4 py-3.5 border-b border-brass-hairline-faint cursor-pointer hover:pl-2 transition-all ${FOCUS}`}
            >
              <span className="font-plex text-xs text-brass">{sectionNumber(i)}</span>
              <span className="flex-1">
                <span className="block font-display text-[18px] text-parchment">{row.title}</span>
                <span className="block text-[12.5px] text-chamber-muted-dim">{row.sub}</span>
              </span>
              <span aria-hidden="true" className="text-brass">
                →
              </span>
            </a>
          ))}
        </aside>
      </div>
    </section>
  );
}
